import React from "react";
import { t } from "@auto/services";
import { can } from "@src/security/permissions";
import * as api from "./electoral-geography/api";
import styles from "./VotingCenterRedirectionPage.module.styl";

const PAGE_SIZE = 20;
const printable = (value) => value ?? "";
const number = (value) => Number(value || 0).toLocaleString();
const title = (center) => [center?.code, center?.name].filter(Boolean).join(" - ") || `#${center?.gid}`;
const region = (center) => [center?.sectionName, center?.communeName, center?.departmentName]
  .filter(Boolean).join(" / ");

const CenterPicker = ({ role, selected, other, onSelect, departments, revision, locked }) => {
  const [search, setSearch] = React.useState("");
  const [department, setDepartment] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [response, setResponse] = React.useState({ items: [], total: 0 });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const isSource = role === "SOURCE";

  React.useEffect(() => { setPage(0); }, [search, department, other?.gid]);

  React.useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const results = await api.browseRedirectionCenters({
          role, search, departmentGid: department,
          excludeGid: other?.gid || 0, offset: page * PAGE_SIZE, limit: PAGE_SIZE,
        });
        if (active) setResponse(results || { items: [], total: 0 });
      } catch (err) {
        if (active) {
          setError(err?.message || String(err));
          setResponse({ items: [], total: 0 });
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 220);
    return () => { active = false; clearTimeout(timer); };
  }, [role, search, department, page, other?.gid, revision]);

  const total = Number(response?.total || 0);
  const items = Array.isArray(response?.items) ? response.items : [];
  const first = total ? page * PAGE_SIZE + 1 : 0;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <section className={`${styles.picker} ${isSource ? styles.sourcePicker : styles.targetPicker}`}>
      <div className={styles.pickerHeader}>
        <div className={styles.stepNumber}>{isSource ? "01" : "02"}</div>
        <div className={styles.pickerHeading}>
          <h2>{t(isSource ? "geo.redirect.source" : "geo.redirect.target")}</h2>
          <span>{t(isSource ? "geo.redirect.sourceHint" : "geo.redirect.targetHint")}</span>
        </div>
        {selected ? <span className={styles.selectedMark} aria-label={t("geo.redirect.selected")}>✓</span> : null}
      </div>

      {selected ? (
        <div className={styles.chosen}>
          <div className={styles.chosenText}>
            <span>{t("geo.redirect.selected")}</span>
            <strong title={title(selected)}>{title(selected)}</strong>
          </div>
          <button type="button" className={styles.clearButton}
            onClick={() => onSelect(null)} disabled={locked} aria-label={t("geo.redirect.clear")}
            title={t("geo.redirect.clear")}>×</button>
        </div>
      ) : null}

      <div className={styles.searchRow}>
        <div className={styles.searchWrap}>
          <span aria-hidden="true" className={styles.searchSymbol}>⌕</span>
          <input type="search" value={search} disabled={locked} onChange={(e) => setSearch(e.target.value)}
            aria-label={t("geo.redirect.search")} placeholder={t("geo.redirect.search")} />
        </div>
        <select aria-label={t("geo.redirect.department")} value={department} disabled={locked}
          onChange={(e) => setDepartment(Number(e.target.value))}>
          <option value={0}>{t("geo.redirect.allDepartments")}</option>
          {departments.map((item) => <option key={item.gid} value={item.gid}>{item.name || item.code}</option>)}
        </select>
      </div>

      <div className={styles.resultsLabel} aria-live="polite">
        <span>{t("geo.redirect.centerCount").replace("{count}", number(total))}</span>
        {loading ? <span className={styles.loading}>{t("common.loading.short")}</span> : null}
      </div>
      {error ? <div className={styles.inlineError} role="alert">{error}</div> : null}
      <div className={styles.centerResults} aria-busy={loading}>
        {!loading && !items.length ? <div className={styles.empty}>{t("geo.redirect.noResults")}</div> : null}
        {items.map((item) => {
          const chosen = Number(selected?.gid) === Number(item.gid);
          const available = item.eligible === true;
          const disabled = !available || locked;
          const reason = item.ineligibleReason ? t(`geo.redirect.reason.${item.ineligibleReason}`) : "";
          const active = item.status == null || Number(item.status) === 1;
          return (
            <button type="button" key={item.gid} disabled={disabled}
              className={`${styles.centerRow} ${chosen ? styles.centerRowSelected : ""}`}
              onClick={() => onSelect(item)} title={disabled ? reason : title(item)}
              aria-pressed={chosen}>
              <span className={`${styles.centerIndicator} ${active ? styles.indicatorActive : styles.indicatorDisabled}`} />
              <span className={styles.centerBody}>
                <span className={styles.centerPrimary}><strong>{printable(item.code) || `#${item.gid}`}</strong>{item.name || ""}</span>
                <span className={styles.centerPlace}>{region(item) || t("geo.redirect.noLocation")}</span>
                {disabled ? <span className={styles.centerReason}>{reason}</span> : null}
              </span>
              {chosen ? <span className={styles.rowCheck}>✓</span> : null}
            </button>
          );
        })}
      </div>
      <div className={styles.pagination}>
        <span>{first}–{last} / {number(total)}</span>
        <div className={styles.pageActions}>
          <button type="button" disabled={loading || page === 0}
            aria-label={t("geo.redirect.previous")}
            onClick={() => setPage((p) => Math.max(0, p - 1))}>←</button>
          <button type="button" disabled={loading || (page + 1) * PAGE_SIZE >= total}
            aria-label={t("geo.redirect.next")}
            onClick={() => setPage((p) => p + 1)}>→</button>
        </div>
      </div>
    </section>
  );
};

const ModeChoice = ({ mode, onChange, locked }) => (
  <div className={styles.modeControl} role="group" aria-label={t("geo.redirect.operation")}>
    <button type="button" className={mode === "MERGE" ? styles.modeActive : ""}
      aria-pressed={mode === "MERGE"} disabled={locked} onClick={() => onChange("MERGE")}>{t("geo.mergeCenters")}</button>
    <button type="button" className={mode === "CLOSE" ? styles.modeActive : ""}
      aria-pressed={mode === "CLOSE"} disabled={locked} onClick={() => onChange("CLOSE")}>{t("geo.closeAndRedirect")}</button>
  </div>
);

const VotingCenterRedirectionPage = ({ gui }) => {
  const [mode, setMode] = React.useState(() =>
    new URLSearchParams(window.location.search).get("mode") === "CLOSE" ? "CLOSE" : "MERGE");
  const [source, setSource] = React.useState(null);
  const [target, setTarget] = React.useState(null);
  const [departments, setDepartments] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [summaryLoading, setSummaryLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [revision, setRevision] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    api.loadDepartments()
      .then((rows) => { if (active) setDepartments((rows || []).slice().sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")))); })
      .catch((err) => { if (active) setError(err?.message || String(err)); });
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    const gid = Number(new URLSearchParams(window.location.search).get("sourceGid"));
    if (!Number.isSafeInteger(gid) || gid <= 0) return;
    let active = true;
    api.browseRedirectionCenters({ role: "SOURCE", gid, limit: 1 })
      .then((response) => {
        const found = response?.items?.find((item) => Number(item.gid) === gid);
        if (active && found) {
          if (found.eligible) setSource(found);
          else setError(t("geo.redirect.invalidPrefill"));
        }
      })
      .catch((err) => { if (active) setError(err?.message || String(err)); });
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (!source?.gid) { setSummary(null); setSummaryLoading(false); return; }
    let active = true;
    setSummary(null);
    setSummaryLoading(true);
    api.votingCenterSummary(source.gid)
      .then((data) => { if (active) setSummary(data); })
      .catch((err) => { if (active) setError(err?.message || String(err)); })
      .finally(() => { if (active) setSummaryLoading(false); });
    return () => { active = false; };
  }, [source?.gid, revision]);

  const chooseSource = (next) => {
    setResult(null);
    setError("");
    setSource(next);
    if (next?.gid && next.gid === target?.gid) setTarget(null);
  };
  const chooseTarget = (next) => {
    setResult(null);
    setError("");
    setTarget(next);
    if (next?.gid && next.gid === source?.gid) setSource(null);
  };
  const canSwap = !saving && !result && source && target && source.canTarget && target.canSource;
  const swap = () => {
    if (!canSwap) return;
    setSource(target);
    setTarget(source);
    setSummary(null);
    setResult(null);
    setError("");
  };
  const canSubmit = Boolean(source?.gid && target?.gid && source.gid !== target.gid
    && summary && !summary.targetVotingCenterGid && !summaryLoading
    && !saving && !result && source.canSource && target.canTarget);
  const confirm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      const next = await api.redirectVotingCenter(source.gid, target.gid, mode);
      setResult(next);
      setRevision((count) => count + 1);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };
  const reset = () => {
    setSource(null);
    setTarget(null);
    setSummary(null);
    setResult(null);
    setError("");
    setRevision((count) => count + 1);
  };

  if (!can(gui, "UPDATE_VOTING_CENTER")) return <div className={styles.unauthorized}>403</div>;
  return (
    <main className={styles.page}>
      <header className={styles.topBar}>
        <div className={styles.titleLine}>
          <a href="/electoral-geography" className={styles.backLink}>← {t("app.menu.electoral_geography.label")}</a>
          <span className={styles.separator}>/</span>
          <strong>{t("geo.redirect.menu")}</strong>
        </div>
        <ModeChoice mode={mode} locked={saving || Boolean(result)} onChange={setMode} />
      </header>

      <div className={styles.workspace}>
        <CenterPicker role="SOURCE" selected={source} other={target}
          departments={departments} onSelect={chooseSource} revision={revision}
          locked={saving || Boolean(result)} />
        <div className={styles.bridge}>
          <span className={styles.bridgeLine} />
          <button type="button" onClick={swap} disabled={!canSwap}
            aria-label={t("geo.redirect.swap")} title={t("geo.redirect.swap")}>⇄</button>
          <span className={styles.bridgeLine} />
        </div>
        <CenterPicker role="TARGET" selected={target} other={source}
          departments={departments} onSelect={chooseTarget} revision={revision}
          locked={saving || Boolean(result)} />
      </div>

      <section className={styles.review} aria-label={t("geo.redirect.preview")}>
        <div className={styles.reviewTop}>
          <div className={styles.reviewTitle}>
            <span className={styles.reviewIcon}>↗</span>
            <div>
              <strong>{t("geo.redirect.preview")}</strong>
              <span>{mode === "MERGE" ? t("geo.redirect.mergeEffect") : t("geo.redirect.closeEffect")}</span>
            </div>
          </div>
          <div className={styles.movement}>
            <span>{source ? title(source) : t("geo.redirect.sourcePlaceholder")}</span>
            <span className={styles.movementArrow}>→</span>
            <span>{target ? title(target) : t("geo.redirect.targetPlaceholder")}</span>
          </div>
        </div>
        <div className={styles.reviewBottom}>
          <div className={styles.impact}>
            <div><strong>{summaryLoading ? "…" : source ? number(summary?.voterCount) : "—"}</strong><span>{t("geo.votersToMove")}</span></div>
            <div><strong>{summaryLoading ? "…" : source ? number(summary?.applicationCount) : "—"}</strong><span>{t("geo.applicationsToMove")}</span></div>
          </div>
          <div className={styles.execution}>
            <span>{t("geo.redirect.nextSteps")}</span>
            <button type="button" className={styles.confirm} disabled={!canSubmit} onClick={confirm}>
              {saving ? t("geo.redirect.saving") : t("geo.redirect.confirm")}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </section>

      {error ? <div className={styles.errorBanner} role="alert">{error}</div> : null}
      {result ? (
        <div className={styles.success} role="status">
          <span className={styles.successSymbol}>✓</span>
          <div>
            <strong>{t("geo.redirect.success")}</strong>
            <p>{t("geo.redirect.successDetail")
              .replace("{voters}", number(result.pendingVoters))
              .replace("{applications}", number(result.referencingApplications))}</p>
          </div>
          <button type="button" onClick={reset}>{t("geo.redirect.newOperation")}</button>
        </div>
      ) : null}
    </main>
  );
};

export const displayVotingCenterRedirection = (gui) => ({
  uuid: "voting-center-redirection",
  view: () => <VotingCenterRedirectionPage gui={gui} />,
});

export default VotingCenterRedirectionPage;
